const readPublicConfig = (key) => {
  try {
    return String(process.env[key] || "").trim();
  } catch (_error) {
    return "";
  }
};

const CONTROLLER_NAME =
  readPublicConfig("REACT_APP_PRIVACY_CONTROLLER_NAME") ||
  "Joz Krupa";
const PRIVACY_CONTACT =
  readPublicConfig("REACT_APP_PRIVACY_CONTACT") || "joz@meetjoz.com";
const SUPERVISORY_AUTHORITY =
  readPublicConfig("REACT_APP_PRIVACY_SUPERVISORY_AUTHORITY") ||
  "your local data protection authority";
const TRANSFER_SAFEGUARDS =
  readPublicConfig("REACT_APP_PRIVACY_TRANSFER_SAFEGUARDS") ||
  "where a provider processes data outside the EEA, an applicable adequacy decision or EU Standard Contractual Clauses with appropriate supplementary measures";
const LEGAL_BASES =
  readPublicConfig("REACT_APP_PRIVACY_LEGAL_BASES") ||
  "legitimate interests under Article 6(1)(f) for operating, securing, and improving the service; Article 6(1)(b) for requested callback or contact steps; and Article 6(1)(c) for statutory privacy-request records";

export const PRIVACY_POLICY_LAST_UPDATED = "9 August 2026";

export const PRIVACY_POLICY_CHAT_SUMMARY =
  "Joz MAXX is an AI system. It may process your questions, voice transcripts, interaction context, and information submitted through app features. Read the Privacy tab for the controller, purposes, rights, processors, retention, and transfer information. For privacy requests, contact joz@meetjoz.com.";

export const PRIVACY_POLICY_SECTIONS = [
  {
    title: "Controller and contact",
    paragraphs: [
      `Controller: ${CONTROLLER_NAME}. Privacy contact: ${PRIVACY_CONTACT}.`,
      `This notice applies to the Joz MAXX experience at meetjoz.com and its supporting API features. It should be read together with the Terms tab and the applicable cookie or browser-storage notice, if any.`,
    ],
  },
  {
    title: "Personal data processed",
    paragraphs: [
      "Depending on the feature, Joz MAXX may process chat questions and answers, voice transcripts, microphone-use state, conversation and session identifiers, consultant or assessment information, callback details such as name, email and phone number, privacy-request details, technical request metadata, and security or rate-limiting data such as IP address and user-agent information.",
      "Please do not submit special-category data, confidential business information, authentication credentials, payment-card data, or information about another person unless the controller has expressly authorised that use and the required safeguards are in place.",
    ],
  },
  {
    title: "Purposes and legal bases",
    paragraphs: [
      "The controller uses data to provide the requested Joz MAXX interaction and related features, maintain session continuity, respond to callback or contact requests, provide privacy-rights handling, prevent abuse, secure the service, troubleshoot reliability, and comply with legal obligations.",
      `Legal bases: ${LEGAL_BASES}. The controller must distinguish processing necessary to provide a requested service, legitimate interests for security and reliability, consent where consent is actually used, and legal obligation for statutory records. No data should be used for a new purpose without the required compatibility assessment or new notice.`,
    ],
  },
  {
    title: "AI processing and human responsibility",
    paragraphs: [
      "You are interacting with Joz MAXX, an AI system. Outputs may be incomplete, inaccurate, or unsuitable for your situation. Joz MAXX is intended for informational and decision-support use; a person must review outputs and remains responsible for consequential decisions and actions.",
      "Joz MAXX is not intended to make or recommend decisions about a person's hiring, employment, pay, promotion, dismissal, credit, insurance, healthcare, education, benefits, immigration, law-enforcement status, biometric identity, or emotional state. Do not use an output as the sole basis for a decision about an identifiable person.",
      "The app does not claim formal EU AI Act compliance or certification through this notice. Article 50 transparency controls, output marking where applicable, risk classification, human oversight, incident handling, and other obligations must be assessed for the actual deployment and use case.",
    ],
  },
  {
    title: "Processors and recipients",
    paragraphs: [
      "Depending on deployment configuration, data may be handled by the application host, database provider, AI or speech provider, email-delivery provider, security provider, and professional advisers or authorities where legally required. The controller must maintain the current vendor list, processor agreements, subprocessor information, access controls, and deletion instructions.",
      "The current runtime can expose configured processors and retention settings through the privacy metadata endpoint. A provider must not receive production personal data until the controller has approved the relevant data-processing terms and security review.",
    ],
  },
  {
    title: "International transfers",
    paragraphs: [
      `Some providers may process data outside the EEA. Where this occurs, ${TRANSFER_SAFEGUARDS}. You may contact ${PRIVACY_CONTACT} to request information about the applicable safeguard.`,
    ],
  },
  {
    title: "Retention",
    paragraphs: [
      "Retention is limited to the period needed for the stated purpose and applicable legal or security requirements. The published application schedule is 30 days for conversations, 30 days for callback requests, and 365 days for privacy-request records. If the deployment schedule changes, this notice must be updated before that change takes effect.",
      "Temporary browser session or local-storage identifiers may remain on the device until removed by the feature, browser, or user. Provider backups, security logs, and legal records may follow separate documented schedules.",
    ],
  },
  {
    title: "Your GDPR rights",
    paragraphs: [
      "Subject to the conditions and exceptions in the GDPR, you may request access, rectification, erasure, restriction of processing, data portability, or object to processing based on legitimate interests. Where processing relies on consent, you may withdraw consent at any time without affecting earlier lawful processing. You may also complain to the competent supervisory authority.",
      "To exercise a right, email joz@meetjoz.com with the request type and enough information to locate the data. The controller may request proportionate identity-verification information. The app provides export, deletion, and general privacy-request handling, but manual review may be required. Requests should be handled without undue delay and normally within one month, subject to lawful extensions or refusal reasons.",
    ],
  },
  {
    title: "Automated decisions and profiling",
    paragraphs: [
      "Joz MAXX is not intended to make solely automated decisions that produce legal effects or similarly significant effects concerning a person, and it must not be used for that purpose. It does not replace human review or create a right to rely on a generated recommendation.",
    ],
  },
  {
    title: "Complaints and updates",
    paragraphs: [
      `You may contact the controller first at ${PRIVACY_CONTACT}. You may also complain to ${SUPERVISORY_AUTHORITY}. This notice will be reviewed when processing, vendors, retention, legal bases, or AI functionality changes. The last-updated date is shown above.`,
    ],
  },
];

export const AI_OVERVIEW_LAST_REVIEWED = "9 August 2026";

export const AI_OVERVIEW_SECTIONS = [
  {
    title: "You are interacting with AI",
    paragraphs: [
      "Joz MAXX is an AI system. It can generate text responses, route requests, use structured application context, and support voice interaction. AI-generated responses are marked in the interface and API metadata where applicable.",
    ],
  },
  {
    title: "Purpose and limits",
    paragraphs: [
      "Joz MAXX explains Joz's work and helps frame organisational AI, data, governance, oversight, and adoption problems. It is not intended for high-impact individual decisions or autonomous consequential action.",
    ],
  },
  {
    title: "Human oversight and accuracy",
    paragraphs: [
      "A person must check outputs, provide context, make consequential decisions, and take action. Do not treat a confidence score as proof. Verify important information with authoritative sources and qualified professionals.",
    ],
  },
  {
    title: "Compliance status",
    paragraphs: [
      "This is an AI transparency and governance summary, not a legal determination, conformity assessment, or certification. EU AI Act obligations depend on the provider, deployer, system classification, data, users, and actual use case.",
    ],
  },
];

export const TERMS_SECTIONS = [
  {
    title: "Operator and acceptance",
    paragraphs: [
      `Joz MAXX is operated by ${CONTROLLER_NAME}. By using the experience, you agree to use it lawfully and consistently with these terms, the Privacy tab, and any feature-specific instructions. If you do not agree, do not use the experience.`,
    ],
  },
  {
    title: "Permitted use",
    paragraphs: [
      "Use Joz MAXX as an exploratory, informational, and decision-support tool. You are responsible for checking outputs, supplying appropriate context, protecting confidential information, and deciding whether an output is suitable for your situation.",
      "It must not be used to make or recommend decisions about credit, insurance, healthcare, education, benefits, immigration, law enforcement, biometric identity, or emotional state.",
      "Do not use Joz MAXX to make or recommend high-impact decisions about people, to infer sensitive traits, to impersonate a person, to submit unlawful or harmful content, to test another person's data without authority, or to bypass security, access controls, rate limits, or human approval.",
    ],
  },
  {
    title: "AI output disclaimer",
    paragraphs: [
      "Joz MAXX can make mistakes, omit relevant context, produce biased or incomplete answers, and change over time. It does not provide legal, medical, financial, employment, immigration, or other regulated professional advice. Obtain qualified advice before relying on an output in a consequential context.",
    ],
  },
  {
    title: "Voice, submissions, and third parties",
    paragraphs: [
      "You control whether to grant microphone permission. Do not record or submit another person's voice or information without the required authority. Submitted content may be sent to service providers needed to operate the requested feature, as described in the Privacy tab.",
    ],
  },
  {
    title: "Availability, security, and changes",
    paragraphs: [
      "The experience may be changed, suspended, rate-limited, or discontinued. No service can guarantee uninterrupted availability or error-free security. Do not use Joz MAXX as the sole system of record, safety control, or emergency service.",
      "The operator may update these terms when the service, law, vendors, or processing changes. The updated date will be shown in the panel. Continued use after an update means the updated terms apply to future use, subject to mandatory consumer and data-protection rights.",
    ],
  },
  {
    title: "Intellectual property and lawful rights",
    paragraphs: [
      "The app, branding, interface, software, and underlying materials remain subject to their applicable intellectual-property rights. You retain rights you already have in content you submit, subject to the permissions needed to operate the requested service. Nothing in these terms removes rights that cannot lawfully be excluded.",
    ],
  },
  {
    title: "Contact and governing terms",
    paragraphs: [
      `For support, privacy, responsible-use, accessibility, or legal questions, contact ${PRIVACY_CONTACT}. These terms do not limit mandatory consumer, privacy, or other rights that apply to you.`,
    ],
  },
];
